import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import useFetchData from 'modules/attendance/components/SettingItemPage/TimeTracking/TimeTrackingPolicy/useFetchData'
import { useDeleteCustomTimeRangeCategory } from 'modules/attendance/hooks/useDeleteCustomTimeRangeCategory'
import {
  CustomTimeRangeCategory,
  useLazyFetchCustomTimeRangeCategories,
} from 'modules/attendance/hooks/useFetchCustomTimeRangeCategories'

interface Props {
  category: CustomTimeRangeCategory
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const DeleteCategoryModal = ({ category, setDeleteModalOpen }: Props) => {
  const { deleteCategory, loading } = useDeleteCustomTimeRangeCategory()
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const { fetch: fetchLazyCustomTimeRangeCategories } = useLazyFetchCustomTimeRangeCategories()
  const { currentSetting } = useFetchData(category.attendanceEmployeesSettingId)
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const settingId = newSetting.id
    const newSettingsCustomTimeRangeCategories = await fetchLazyCustomTimeRangeCategories(settingId)

    const newCategory = newSettingsCustomTimeRangeCategories.customTimeRangeCategories.find(
      (newSettingCategory) =>
        newSettingCategory.timeSettingsCustomTimeRangeCategoryId ===
        category.timeSettingsCustomTimeRangeCategoryId
    )

    const newPayload = {
      id: newCategory!.id,
    }

    onSubmit(newPayload)

    const routeParams = {
      id: 'time_tracking',
      settingId: settingId.toString(),
      tabId: 'time_ranges',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onSubmit = async (payload: { id: string }) => {
    const result = await deleteCategory(payload)
    const handled = handleResult(result, () => undefined)
    if (handled.success && payload.id === category.id) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }
    setDeleteModalOpen(false)
    return handled
  }

  const onUpdate = () => {
    return onSubmit({ id: category.id })
  }

  const name = category.name

  return (
    <>
      <Modal
        illustration="alert"
        title={i18n.t('time_tracking.settings.time_blocks.categories.modals.delete_title_text', {
          name,
        })}
        description={i18n.t('time_tracking.settings.time_blocks.categories.modals.delete_text', {
          name,
        })}
        onClose={() => setDeleteModalOpen(false)}
        onSubmit={() => setOpenEffectiveAtFormModal(true)}
        footer={
          <>
            <Button type="negative" disabled={loading} submit>
              {i18n.t('common.remove')}
            </Button>
            <Button type="positive" onClick={() => setDeleteModalOpen(false)}>
              {i18n.t('common.keep')}
            </Button>
          </>
        }
      />
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onUpdate}
          onClose={() => setDeleteModalOpen(false)}
          running={false}
          attendanceSettingId={currentSetting?.attributes.id}
          attendanceSettingEffectiveAt={currentSetting?.attributes.effective_at}
        />
      )}
    </>
  )
}

export default DeleteCategoryModal
